import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  LinearProgress
} from '@material-ui/core';
import { BackdropCircular, TextFormattedNumber } from 'components';
import {
  publishPushCartItemsApi,
  createActiveCartCombinedAPI,
  openCheckoutServiceApi,
  downloadQuotePdfApi,
  createDraftQuoteApi,
  createQuotePdfPreviewApi
} from 'api';
import { setFinalPageData } from 'redux/actions/quote';
import { useStyles } from '../styles';
import { calculateTotals } from '../utils/calculateTotals';
import QuotedSummaryItem from './QuotedSummaryItem';
import QuoteNotesDialog from './QuoteNotesDialog';


const QuoteSummaryCard = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<{id?: string}>();

  const {
    currentQuoteCustomer,
    customerPushCart,
    quotePdfPreview,
    loadingQuotePdfDownload,
    loadingQuotePdfPreview,
    finalPageData,
    loadingDraftedQuotes
  } = useSelector((state: IReduxState) => state.quote);

  const { items = [] } = finalPageData || {};

  const [openPreviewQuote, setOpenPreviewQuote] = useState(false);
  const [notesDialog, setNotesDialog] = useState({isOpen: false, dialogType: ''});
  const [quoteData, setQuoteData] = useState({
    quoteNotes: '',
    expirationDate: null,
    quoteName: '',
    quoteDiscount: { price_type: '', price: null }
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    discountTotal: 0,
    grandTotal: 0,
    totalSubscription: 0
  });

  useEffect(() => {
    if (items) {
      const transformedProducts = [];

      for (const key in customerPushCart.products) {
        if (customerPushCart.products.hasOwnProperty(key)) {
          transformedProducts.push(customerPushCart.products[key].product);
        }
      }

      const totals = calculateTotals(items, transformedProducts);
      setTotals(totals);
    }
  }, [items]);

  const handleCreateQuote = async ({quoteNotes, expirationDate, startDate = null, mustUsedDate = null, quoteName, quoteDiscount}) => {
    setNotesDialog({isOpen: false, dialogType: ''});

    const publishData = {
      selectedItems: items,
      expirationDate,
      startDate,
      mustUsedDate,
      quoteName,
      patient_notice: quoteNotes,
      quoteDiscount
    };

    const isItemsInPushCart = await publishPushCartItemsApi({
      dispatch,
      pushCartId: customerPushCart._id,
      publishData
    });

    if (!isItemsInPushCart) {
      history.push({
        pathname: `/quoting/profile/${id}`
      });
    } else {
      history.push({
        pathname: `/quoting/profile/${id}/quote-workspace/summary`
      });
    }

    dispatch(setFinalPageData([], 30));
  };

  const handleDownloadPdf = async () => {
    const {quoteNotes, expirationDate, quoteName, quoteDiscount} = quoteData;
    const selectedItemsIds = items.map((i) => i._id);
    downloadQuotePdfApi({dispatch, id: customerPushCart._id, selectedItemsIds, quoteNotes, expirationDate, quoteName, quoteDiscount});
  };

  const handlePreviewQuote = async ({quoteNotes, expirationDate, quoteName, quoteDiscount}) => {
    setQuoteData({quoteNotes, expirationDate, quoteName, quoteDiscount});
    const selectedItemsIds = items.map((i) => i._id);
    await createQuotePdfPreviewApi({dispatch, id: customerPushCart._id, selectedItemsIds, quoteNotes, expirationDate, quoteName, quoteDiscount});
    setNotesDialog({isOpen: false, dialogType: ''});
    setOpenPreviewQuote(true);
  };

  const selectedItems = Object.values(items).map(({ _id, ...data }) => ({ ...data }));

  const handleProceedToPayNow = async ({ quoteDiscount }) => {
    const activeCartId = await createActiveCartCombinedAPI({
      dispatch,
      customerId: currentQuoteCustomer.id,
      selectedItems,
      quoteDiscount
    });

    if (activeCartId) {
      openCheckoutServiceApi({ dispatch, email: currentQuoteCustomer.email, pushCartId: customerPushCart._id });
    }

    dispatch(setFinalPageData([], 30));
    setNotesDialog({isOpen: false, dialogType: ''});
  };

  const handleSaveQuote = async () => {
    setNotesDialog({isOpen: false, dialogType: ''});
    const publishData = {
      selectedItems: items
    };

    const isPushCartEmpty = await createDraftQuoteApi({
      dispatch,
      pushCartId: customerPushCart._id,
      pushCart: publishData
    });

    dispatch(setFinalPageData([], 30));

    if (isPushCartEmpty) {
      history.push(`/quoting/profile/${id}`);
    }
  };

  const handleConfirmNotesDialog = (quoteData) => {
    if (notesDialog.dialogType === 'createQuote') {
      handleCreateQuote(quoteData);
    } else if (notesDialog.dialogType === 'previewQuote') {
      handlePreviewQuote(quoteData);
    } else if (notesDialog.dialogType === 'payNow') {
      handleProceedToPayNow(quoteData);
    }
  };

  return (
    <Card className={`${classes.bgBlue} root-card`}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        p={2}
        border={1}
        borderRadius={4}
        borderColor="grey.300"
      >
        <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
          <Button
            className={classes.customQuoteSummaryButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={(Object.keys(items).length === 0)}
            onClick={() => setNotesDialog({isOpen: true, dialogType: 'createQuote'})}
            fullWidth
          >
            Create a Quote
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
          <Button
            className={classes.customQuoteSummaryButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={(Object.keys(items).length === 0)}
            fullWidth
            onClick={handleSaveQuote}
          >
            Save for Later
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
          <Button
            className={classes.customQuoteSummaryButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={(Object.keys(items).length === 0)}
            fullWidth
            onClick={() => setNotesDialog({isOpen: true, dialogType: 'payNow'})}
          >
            Pay Now
          </Button>
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
          <Button
            className={classes.customQuoteSummaryButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={(Object.keys(items).length === 0)}
            fullWidth
            onClick={() => setNotesDialog({isOpen: true, dialogType: 'previewQuote'})}
          >
            Preview Quote
          </Button>
        </Box>

        <Divider style={{ width: '100%', margin: '16px 0' }} />

        <Grid container spacing={3} className="py-4">
          <Grid item xs={12} className="py-2">
            <QuotedSummaryItem
              label="Subtotal"
              value={totals.subtotal}
              prefix="$"
            />
            <QuotedSummaryItem
              label="Total Savings"
              value={totals.discountTotal}
              prefix="$"
            />
            <div className="flex items-center justify-between mt-5 mb-2">
              <Typography variant="h4" className={`font-bold ${classes.customQuoteSummaryTypography}`}>
                TOTAL
              </Typography>
              <Typography variant="h4" align="right" className={`font-bold ${classes.customQuoteSummaryTypography}`}>
                <TextFormattedNumber
                  value={totals.grandTotal}
                  prefix="$"
                />
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Box>
      {notesDialog.isOpen &&
        <QuoteNotesDialog
          isOpen={notesDialog.isOpen}
          onClose={() => setNotesDialog({isOpen: false, dialogType: ''})}
          onConfirm={handleConfirmNotesDialog}
          dialogType={notesDialog.dialogType}
          total={totals.grandTotal}
        />
      }
      <Dialog
        open={openPreviewQuote}
        onClose={() => setOpenPreviewQuote(false)}
        maxWidth={'md'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" className="mt-4">Preview Quote</Typography>
        </DialogTitle>
        <DialogContent  className="m-4">
          {loadingQuotePdfPreview ? <LinearProgress /> : <div dangerouslySetInnerHTML={{ __html: quotePdfPreview || ''}} />}
        </DialogContent>
        <DialogActions>
          <Button
            className="max-w-190"
            size="small"
            variant="contained"
            color="primary"
            disabled={(Object.keys(items).length === 0)}
            fullWidth
            onClick={() => {
              handleCreateQuote(quoteData);
              setOpenPreviewQuote(false);
            }}
          >
            <Icon fontSize="small">send</Icon>
            <span className="ml-2">CREATE A QUOTE</span>
          </Button>
          <Button
            className="max-w-190 mr-4"
            size="small"
            variant="contained"
            color="primary"
            disabled={(Object.keys(items).length === 0)}
            fullWidth
            onClick={handleDownloadPdf}
          >
            <Icon fontSize="small">download</Icon>
            <span className="ml-2">DOWNLOAD</span>
          </Button>
        </DialogActions>
        <BackdropCircular loading={loadingQuotePdfDownload || loadingDraftedQuotes} />
      </Dialog>
    </Card>
  );
};

export default QuoteSummaryCard;
